/*
 * magic-nav (魔法导航)
 * Copyright (C) 2026 魔法代码
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 *
 * SPDX-License-Identifier: GPL-3.0-or-later
 */

// 身份认证模块：登录、Token 存取、携带 Authorization 的请求封装
// 后端登录接口：POST /api/nav  action=login（返回 { data: { type:'token', id, attributes:{ token } } }）

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const LINK_URL = `${API_BASE_URL}/nav`
const TOKEN_KEY = 'magic_nav_token'

// ===== Token 本地存取 =====
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function isLoggedIn(): boolean {
  return !!getToken()
}

interface LoginResult {
  token: string
}

interface JsonApiErrorDoc {
  errors?: Array<{ detail?: string; title?: string }>
}

/**
 * 管理员登录
 * @throws Error 登录失败时抛出后端 detail 或通用错误
 */
export async function login(password: string): Promise<LoginResult> {
  const response = await fetch(LINK_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/json',
    },
    // 单用户模式，用户名固定为 admin
    body: JSON.stringify({ action: 'login', username: 'admin', password }),
  })

  const json = await response.json()
  if (!response.ok) {
    const err = (json as JsonApiErrorDoc).errors?.[0]
    throw new Error(err?.detail || err?.title || `登录失败（HTTP ${response.status}）`)
  }

  const token = json?.data?.attributes?.token
  if (!token) {
    throw new Error('登录响应缺少 token')
  }
  setToken(token)
  return { token }
}

/**
 * 带 Authorization 的 JSON:API 请求封装
 * 用于管理后台写接口；401 时清空 Token 并跳转登录页。
 */
export async function authRequest<T>(method: string, body?: unknown): Promise<T> {
  const token = getToken()
  const response = await fetch(LINK_URL, {
    method,
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (response.status === 401) {
    clearToken()
    // 避免循环跳转：仅在非登录页时跳转
    if (location.pathname !== '/login') {
      location.href = '/login'
    }
    throw new Error('登录已失效，请重新登录')
  }

  const json = await response.json()
  if (!response.ok) {
    const err = (json as JsonApiErrorDoc).errors?.[0]
    throw new Error(err?.detail || err?.title || `请求失败（HTTP ${response.status}）`)
  }
  return json as unknown as T
}
